import { AutocompleteWithLabel, InputWithLabel } from '@adotel/components'
import { useQuery } from '@apollo/client'
import {
  ButtonBase,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography
} from '@material-ui/core'
import AddDateRangeIcon from '@material-ui/icons/AddCircleOutline'
import { useEffect, useState } from 'react'
import React from 'react'

import TimePeriodForm from '../../components/TimePeriodForm'
import { PRICE_CLASSES_LIST_QUERY } from '../../graphql/priceClass'
import { addDaysToDate, DateObject } from '../../helpers/date'
import { getCurrenciesAvailable } from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import { Currency } from '../../types/currency'
import { actionTypes, PriceClass } from '../../types/priceClass'
import {
  DateObjectForRateConnection,
  RadioButtonType,
  RateConnectionGenerateInput
} from '../../types/rateConnection'
import useStyles from './styles/Filters.styles'

interface FiltersProps {
  selectedDates: DateObject[]
  setSelectedDates: React.Dispatch<React.SetStateAction<DateObject[]>>
  autoCompleteForPriceClass: any
  setAutoCompleteForPriceClass: React.Dispatch<any>
  input: RateConnectionGenerateInput
  setInput: React.Dispatch<React.SetStateAction<RateConnectionGenerateInput>>
  selectedWeekDaysArray: string[][]
  setSelectedWeekDaysArray: React.Dispatch<React.SetStateAction<string[][]>>
  radioButtonValue: RadioButtonType
  setRadioButtonValue: React.Dispatch<React.SetStateAction<RadioButtonType>>
  isRoleAdmin: boolean
}

export default function Filters({
  selectedDates,
  setSelectedDates,
  autoCompleteForPriceClass,
  setAutoCompleteForPriceClass,
  input,
  setInput,
  selectedWeekDaysArray,
  setSelectedWeekDaysArray,
  radioButtonValue,
  setRadioButtonValue,
  isRoleAdmin
}: FiltersProps) {
  const classes = useStyles()
  const [timePickerSelected, setTimePickerSelected] = useState<boolean>(false)
  const [onDoneClickForCloseDatePicker, setOnDoneClickForCloseDatePicker] =
    useState<boolean>(true)
  const [activeDatePickerIndex, setactiveDatePickerIndex] = useState<number>(-1)
  const [rateMultiplierValue, setRateMultiplierValue] = useState<string>()

  const maxVisibleActions = 1
  const LTR = '\u200E' // Left-To-Right Mark

  const { data: priceClassList, loading: loadingForGetPriceClassesList } =
    useQuery<{
      priceClasses: PriceClass[]
    }>(PRICE_CLASSES_LIST_QUERY, {
      notifyOnNetworkStatusChange: true
    })

  useEffect(() => {
    let tempDate: DateObjectForRateConnection[] = []

    selectedDates.map((item, index) => {
      let t: DateObjectForRateConnection = {
        from: item.gregorianStartDate || '',
        to: item.gregorianEndDate || '',
        days: selectedWeekDaysArray[index]
      }

      tempDate.push(t)
    })

    setInput({
      ...input,
      priceClassID:
        radioButtonValue === 'PriceClass'
          ? autoCompleteForPriceClass && autoCompleteForPriceClass.value
          : undefined,
      rateMultiplier:
        radioButtonValue === 'RateMultiplier' ? rateMultiplierValue : undefined,
      date: tempDate,
      outputCurrency:
        radioButtonValue === 'PriceClass'
          ? autoCompleteForPriceClass && autoCompleteForPriceClass.code
          : input.inputCurrency
    })
  }, [
    autoCompleteForPriceClass,
    selectedDates,
    selectedWeekDaysArray,
    radioButtonValue,
    rateMultiplierValue
  ])

  const options = priceClassList?.priceClasses
    ? priceClassList.priceClasses
        .filter(
          (ele) => ele.inputCurrency === input.inputCurrency && !ele.archivedAt
        )
        .map((pr) => {
          const name = lang === 'fa' ? pr.nameTranslatedList[0]?.value : pr.name

          const actions = pr.priceActions
            .slice(0, maxVisibleActions)
            .map((i, index) => {
              const actionType = actionTypes.find(
                (item) => item.value === i.actionType
              )?.label

              const amountText = i.actionAmount.amount
                ? `${LTR}${i.actionAmount.amount}${
                    i.actionAmount.type === 'FixedFee'
                      ? ` ${getTranslate(i.actionAmount.toCurrency || '')}`
                      : '%'
                  }`
                : ''

              const separator =
                index < maxVisibleActions - 1 &&
                index < pr.priceActions.length - 1
                  ? lang === 'fa'
                    ? ' و '
                    : ' + '
                  : ''

              const entityName = i.beneficiary?.entityName
                ? `${getTranslate(' for ')}${i.beneficiary?.entityName}`
                : ''

              return `${amountText} ${getTranslate(
                actionType || ''
              )} ${entityName}${separator}`
            })
            .join('')

          const ellipsis =
            pr.priceActions.length > maxVisibleActions
              ? lang === 'fa'
                ? ' و ...'
                : ' + ...'
              : ''

          return {
            label: `${name} "${actions}${ellipsis}"`,
            value: pr.id || '',
            code: pr.outputCurrency
          }
        })
    : []

  return (
    <div className={classes.root}>
      <div className={classes.providerCurrencyContainer}>
        <Typography>{getTranslate('Provider Currency')}:</Typography>
        <Select
          value={input.inputCurrency || ''}
          onChange={(event) => {
            setInput({
              ...input,
              inputCurrency: event.target.value as Currency,
              outputCurrency:
                radioButtonValue === 'RateMultiplier'
                  ? (event.target.value as Currency)
                  : ''
            })
          }}>
          {getCurrenciesAvailable().map((currency) => (
            <MenuItem
              key={currency}
              value={currency}
              className={classes.menuItemRight}>
              {currency}
            </MenuItem>
          ))}
        </Select>
      </div>
      <RadioGroup
        className={classes.radioGroup}
        value={radioButtonValue}
        onChange={(event) =>
          setRadioButtonValue(event.target.value as RadioButtonType)
        }>
        <FormControlLabel
          value="PriceClass"
          control={<Radio color="primary" />}
          label={getTranslate('Price Class')}
          disabled={!isRoleAdmin}
        />
        <FormControlLabel
          value="RateMultiplier"
          control={<Radio color="primary" />}
          label={getTranslate('Rate Multiplier')}
        />
      </RadioGroup>
      {radioButtonValue === 'PriceClass' ? (
        <AutocompleteWithLabel
          noHelperText
          fullWidth
          value={autoCompleteForPriceClass}
          label={getTranslate('Price Class')}
          placeholder={getTranslate('Select Price Class')}
          inputSize="small"
          options={options}
          onChange={(_, v: any) => {
            if (v) {
              setAutoCompleteForPriceClass({
                value: v.value,
                label: v.label,
                code: v.code
              })
            } else {
              setAutoCompleteForPriceClass(null)
            }
          }}
          getOptionLabel={(option) => option.label || ''}
          getOptionSelected={(option, value) => option.value === value.value}
          loading={loadingForGetPriceClassesList}
          loadingText={getTranslate('Loading') + '...'}
        />
      ) : (
        <InputWithLabel
          label={getTranslate('Rate Multiplier')}
          autoComplete="off"
          placeholder={getTranslate('Enter Rate Multiplier')}
          noHelperText
          fullWidth
          className={[classes.searchInput, classes.inputNumber].join(' ')}
          value={input.rateMultiplier}
          onChange={(e) => {
            const value = e.currentTarget.value

            if (value === '' || Number(value) >= 0) {
              setRateMultiplierValue(value)
            }
          }}
          inputSize="small"
          type="number"
        />
      )}
      <div className={classes.channelCurrencyContainer}>
        <Typography>{getTranslate('Channel Currency')}:</Typography>
        <Select value={input.outputCurrency || ''} disabled>
          {getCurrenciesAvailable().map((currency) => (
            <MenuItem
              key={currency}
              value={currency}
              className={classes.menuItemRight}>
              {currency}
            </MenuItem>
          ))}
        </Select>
      </div>
      <div className={classes.dateSelectorForm}>
        <Typography>{getTranslate('Time Periods')}</Typography>
        {selectedDates.map((dateRangeItem, index) => (
          <TimePeriodForm
            key={index.toString()}
            isOpen={activeDatePickerIndex === index && timePickerSelected}
            toggleDatePicker={() => {
              setactiveDatePickerIndex(
                activeDatePickerIndex === index ? -1 : index
              )
              setOnDoneClickForCloseDatePicker(true)
            }}
            dateObject={dateRangeItem}
            onChange={(value) =>
              setSelectedDates((currentList) => [
                ...currentList.slice(0, index),
                value,
                ...currentList.slice(index + 1)
              ])
            }
            deletable={index !== 0}
            onDelete={() => {
              if (index !== 0) {
                setSelectedDates((currentList) => [
                  ...currentList.slice(0, index),
                  ...currentList.slice(index + 1)
                ])

                setSelectedWeekDaysArray((currentList) => [
                  ...currentList.slice(0, index),
                  ...currentList.slice(index + 1)
                ])
              }
            }}
            onDoneClickForCloseDatePicker={onDoneClickForCloseDatePicker}
            setOnDoneClickForCloseDatePicker={setOnDoneClickForCloseDatePicker}
            setTimePickerSelected={setTimePickerSelected}
            showDaysInFooter
            onSelectedWeekDays={(value: string[]) => {
              setSelectedWeekDaysArray((currentList) => [
                ...currentList.slice(0, index),
                value,
                ...currentList.slice(index + 1)
              ])
            }}
            selectedWeekDays={selectedWeekDaysArray[index]}
            disableAfter={
              selectedDates[index].gregorianStartDate
                ? addDaysToDate(
                    selectedDates[index].gregorianStartDate || '',
                    29
                  )
                : undefined
            }
          />
        ))}
        <ButtonBase
          onClick={() =>
            setSelectedDates((currentList) => [...currentList, {}])
          }>
          <AddDateRangeIcon className={classes.addMoreDateRangeIcon} />
          <Typography>{getTranslate('More Time Periods')}</Typography>
        </ButtonBase>
      </div>
    </div>
  )
}
